"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

export default function InteractiveChallengesSection() {
    const t = useTranslations();

    return (
        <section id="interactive-challenges" className="pt-32">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("InteractiveChallengesSection_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t.rich("InteractiveChallengesSection_intro", {
                    strong: (chunks) => (
                        <strong className="font-semibold text-black dark:text-white">
                            {chunks}
                        </strong>
                    ),
                })}
            </p>

            {/* === COMPOSITION ÉDITORIALE === */}
            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows radiaux statiques */}
                <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-orange-500/20 rounded-full blur-3xl" />
                <div className="absolute -bottom-20 -left-20 w-[400px] h-[400px] bg-purple-500/15 rounded-full blur-3xl" />

                {/* Pattern dots subtil */}
                <div
                    className="absolute inset-0 opacity-[0.06]"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
                        backgroundSize: "32px 32px",
                    }}
                />

                {/* Contenu */}
                <div className="relative grid xl:grid-cols-[1fr_1fr] gap-10 xl:gap-16 p-8 sm:p-12 lg:p-14 items-center">
                    {/* LEFT — Conversation chat BeBot/apprenant */}
                    <div className="relative flex flex-col gap-3 max-w-[460px] mx-auto xl:mx-0 w-full">
                        {/* Header conversation */}
                        <div className="flex items-center justify-between mb-1 px-1">
                            <div className="flex items-center gap-2.5">
                                <div className="relative">
                                    <Image
                                        src="/images/bebot/bebot-profile.jpg"
                                        alt="BeBot"
                                        width={40}
                                        height={40}
                                        className="rounded-full ring-2 ring-white/20 shadow-lg object-cover"
                                    />
                                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-400 ring-2 ring-[#241845]" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-white leading-tight mb-0">
                                        BeBot
                                    </p>
                                    <p className="text-[10px] text-orange-300/80 uppercase tracking-[0.14em] mb-0 leading-tight mt-0.5 font-semibold">
                                        {t(
                                            "InteractiveChallengesSection_chat_status",
                                        )}
                                    </p>
                                </div>
                            </div>
                            <div className="hidden sm:flex items-center gap-1.5 text-[10px] text-gray-400">
                                <SolarIcon
                                    name="shield-check-bold-duotone"
                                    className="w-3.5 h-3.5 text-green-400"
                                />
                                <span>
                                    {t(
                                        "InteractiveChallengesSection_chat_secure",
                                    )}
                                </span>
                            </div>
                        </div>

                        {/* Bulle apprenant 1 */}
                        <div
                            className="flex justify-end"
                            style={{
                                animation:
                                    "fadeIn 0.5s ease-out 0.2s both",
                            }}
                        >
                            <div className="max-w-[80%] rounded-2xl rounded-br-md bg-white/[0.08] ring-1 ring-white/10 px-4 py-2.5">
                                <p className="text-[13px] sm:text-sm text-gray-200 leading-snug mb-0">
                                    {t(
                                        "InteractiveChallengesSection_chat_user_1",
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Bulle BeBot 1 */}
                        <div
                            className="flex justify-start"
                            style={{
                                animation:
                                    "fadeIn 0.5s ease-out 0.9s both",
                            }}
                        >
                            <div className="max-w-[88%] rounded-2xl rounded-bl-md bg-white shadow-xl px-4 py-3">
                                <p className="text-[13px] sm:text-sm text-gray-800 leading-snug mb-0">
                                    {t.rich(
                                        "InteractiveChallengesSection_chat_bebot_1",
                                        {
                                            code: () => (
                                                <code className="font-mono bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded text-[11px] font-semibold">
                                                    &lt;p&gt;
                                                </code>
                                            ),
                                        },
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Bulle apprenant 2 */}
                        <div
                            className="flex justify-end"
                            style={{
                                animation:
                                    "fadeIn 0.5s ease-out 1.7s both",
                            }}
                        >
                            <div className="max-w-[70%] rounded-2xl rounded-br-md bg-white/[0.08] ring-1 ring-white/10 px-4 py-2.5">
                                <p className="text-[13px] sm:text-sm text-gray-200 leading-snug mb-0">
                                    {t(
                                        "InteractiveChallengesSection_chat_user_2",
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Bulle BeBot 2 — avec achievement intégré */}
                        <div
                            className="flex justify-start"
                            style={{
                                animation:
                                    "fadeIn 0.5s ease-out 2.4s both",
                            }}
                        >
                            <div className="max-w-[92%] rounded-2xl rounded-bl-md bg-white shadow-xl overflow-hidden">
                                <p className="text-[13px] sm:text-sm text-gray-800 leading-snug mb-0 px-4 py-3">
                                    {t(
                                        "InteractiveChallengesSection_chat_bebot_2",
                                    )}
                                </p>
                                {/* Mini achievement card */}
                                <div
                                    className="flex items-center gap-3 bg-gradient-to-r from-orange-50 to-amber-50 border-t border-orange-100 px-4 py-2.5"
                                    style={{
                                        animation:
                                            "fadeIn 0.6s ease-out 3s both",
                                    }}
                                >
                                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-orange-400 to-amber-500 shadow-sm">
                                        <SolarIcon
                                            name="cup-star-bold-duotone"
                                            className="w-4 h-4 text-white"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-sm font-bold text-gray-900 leading-none">
                                                95
                                            </span>
                                            <span className="text-[10px] text-gray-500 font-medium">
                                                / 100
                                            </span>
                                            <span className="text-[10px] font-semibold uppercase tracking-wider text-orange-600 ml-1">
                                                {t(
                                                    "InteractiveChallengesSection_score_label",
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-purple-500 to-purple-700 px-2.5 py-1 shadow-sm">
                                        <SolarIcon
                                            name="bolt-bold-duotone"
                                            className="w-3 h-3 text-amber-300"
                                        />
                                        <span className="text-[10px] font-bold text-white">
                                            +50 XP
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT — Moment de coaching textuel */}
                    <div className="relative">
                        <div className="inline-flex items-center gap-2 rounded-full bg-orange-500/15 ring-1 ring-orange-400/30 px-3 py-1 mb-4">
                            <span className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400" />
                            </span>
                            <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-300">
                                {t(
                                    "InteractiveChallengesSection_coaching_eyebrow",
                                )}
                            </span>
                        </div>

                        <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white leading-[1.1] tracking-tight mb-4">
                            {t.rich(
                                "InteractiveChallengesSection_coaching_title",
                                {
                                    highlight: (chunks) => (
                                        <span className="bg-gradient-to-r from-orange-300 to-amber-300 bg-clip-text text-transparent">
                                            {chunks}
                                        </span>
                                    ),
                                },
                            )}
                        </h3>

                        <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-6">
                            {t(
                                "InteractiveChallengesSection_coaching_description",
                            )}
                        </p>

                        {/* Mini-chips "Indice donné · Pas la solution" */}
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-sm ring-1 ring-white/15 px-3 py-1.5">
                                <SolarIcon
                                    name="lightbulb-bold-duotone"
                                    className="w-3.5 h-3.5 text-amber-400"
                                />
                                <span className="text-[11px] font-medium text-gray-200">
                                    {t(
                                        "InteractiveChallengesSection_hint_given",
                                    )}
                                </span>
                            </div>
                            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur-sm ring-1 ring-white/15 px-3 py-1.5">
                                <SolarIcon
                                    name="smile-circle-bold-duotone"
                                    className="w-3.5 h-3.5 text-green-400"
                                />
                                <span className="text-[11px] font-medium text-gray-200">
                                    {t(
                                        "InteractiveChallengesSection_not_solution",
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* === 3 BÉNÉFICES EN GRILLE === */}
            <div className="mt-8 grid sm:grid-cols-3 gap-4 sm:gap-5">
                {[
                    {
                        icon: "code-square-bold-duotone",
                        titleKey:
                            "InteractiveChallengesSection_feature_editor_title",
                        descKey:
                            "InteractiveChallengesSection_feature_editor_desc",
                    },
                    {
                        icon: "chat-round-dots-bold-duotone",
                        titleKey:
                            "InteractiveChallengesSection_feature_bebot_title",
                        descKey:
                            "InteractiveChallengesSection_feature_bebot_desc",
                    },
                    {
                        icon: "cup-star-bold-duotone",
                        titleKey:
                            "InteractiveChallengesSection_feature_score_title",
                        descKey:
                            "InteractiveChallengesSection_feature_score_desc",
                    },
                ].map((f) => (
                    <div
                        key={f.titleKey}
                        className="rounded-2xl border border-gray-200 dark:border-borderDark/80 bg-white dark:bg-bgDark-500 p-5"
                    >
                        <div className="w-10 h-10 rounded-xl bg-orange-500/10 dark:bg-orange-500/15 ring-1 ring-orange-500/20 dark:ring-orange-400/25 flex items-center justify-center mb-4">
                            <SolarIcon
                                name={f.icon}
                                className="w-5 h-5 text-orange-600 dark:text-orange-400"
                            />
                        </div>
                        <h3 className="text-base font-semibold text-black dark:text-white mb-1.5 leading-tight">
                            {t(f.titleKey)}
                        </h3>
                        <p className="text-sm text-black/70 dark:text-lightBlue/80 leading-relaxed mb-0">
                            {t(f.descKey)}
                        </p>
                    </div>
                ))}
            </div>
        </section>
    );
}

